import { clsx } from "clsx";
import type { ReactNode } from "react";

export type ScoreBarProps = {
  label: ReactNode;
  value: number | null;
  max?: number;
  display?: "ratio" | "decimal" | "none";
  tone?: "primary" | "canopy";
  size?: "sm" | "md";
  className?: string;
};

export function ScoreBar({
  label,
  value,
  max = 100,
  display = "none",
  tone = "primary",
  size = "md",
  className,
}: ScoreBarProps) {
  const pct =
    value == null ? 0 : Math.min(100, Math.max(0, (value / max) * 100));

  const rightText =
    value == null || display === "none"
      ? null
      : display === "ratio"
        ? `${value}/${max}`
        : ((value) / 10).toFixed(1);

  return (
    <div className={clsx("grid gap-2", className)}>
      <div className="flex items-center justify-between">
        <span
          className={clsx(
            size === "sm"
              ? "text-xs text-text-muted"
              : tone === "canopy"
                ? "text-sm font-medium text-primary"
                : "text-sm font-semibold text-primary",
          )}
        >
          {label}
        </span>
        {rightText && (
          <span
            className={clsx(
              "tabular-nums",
              display === "decimal" && "font-semibold text-primary text-xs",
            )}
            style={display === "ratio" ? { opacity: 0.75 } : undefined}
          >
            {rightText}
          </span>
        )}
      </div>
      <div
        className={clsx(
          "w-full rounded-full overflow-hidden bg-bush-100",
          size === "sm" ? "h-1.5" : "h-[5px]",
        )}
      >
        <div
          className={clsx(
            "rounded-full bg-primary",
            size === "sm" ? "h-1.5" : "h-[5px]",
            value == null && tone === "primary" && "opacity-25",
          )}
          style={{
            width: `${pct}%`,
            transition: tone === "canopy" ? "width 0.25s ease" : undefined,
          }}
        />
      </div>
    </div>
  );
}
